import React, { useEffect, useState } from 'react';
import { toZonedTime } from 'date-fns-tz';
import endpoints from 'constants/endpoints';
import useFormHandler from 'hooks/useFormHandler';
import useApi from 'hooks/useApi';
import { Button, DynamicForm } from 'components';
import { changeTimezoneAndConvertToUTC } from 'helpers/date';
import { Props } from 'types/modals';
import Base from '../../base';
import schema from './schema';

const EditionEventModal = ({ closing, onClose, params }: Props) => {
  const formHandler = useFormHandler(schema);
  const api = useApi();
  const templateApi = useApi();
  const [venueId, setVenueId] = useState(null);

  const handleSubmit = async () => {
    formHandler.setIsSubmitting(true);
    const { data } = formHandler;
    const { id, timezone } = data.venue;
    const isCapacityExceeded = +data.employeeCapacity < +api.response.data.employeesAllocated;

    const formData = new FormData();

    formData.append('venueId', id ?? '');
    formData.append(
      'allowRegistrationsAt',
      changeTimezoneAndConvertToUTC(data.allowRegistrationsAt, timezone)?.toISO() ?? ''
    );
    formData.append('heldOn', changeTimezoneAndConvertToUTC(data.heldOn, timezone)?.toISO() ?? '');
    if (data.heldOnTimeIsUnknown !== undefined) {
      formData.append('heldOnTimeIsUnknown', String(data.heldOnTimeIsUnknown));
    }
    // required by schema; always present
    formData.append('eventType', String(data.eventType));
    if (data.reportTimeAt)
      formData.append('reportTimeAt', changeTimezoneAndConvertToUTC(data.reportTimeAt, timezone)?.toISO() ?? '');
    if (data.reportTimeIsUnknown !== undefined) {
      formData.append('reportTimeIsUnknown', String(data.reportTimeIsUnknown));
    }
    if (data.employeeCapacity) formData.append('employeeCapacity', String(data.employeeCapacity));
    if (data.buffer) formData.append('buffer', String(data.buffer));
    if (data.automateRoster !== undefined) formData.append('automateRoster', String(data.automateRoster));
    if (data.templateId) formData.append('templateId', String(data.templateId));
    if (data.eventTrainingUrl) formData.append('eventTrainingUrl', data.eventTrainingUrl ?? '');
    if (data.hireflixUrl !== undefined) formData.append('hireflixUrl', data.hireflixUrl ?? '');
    if (data.groupMeUrl !== undefined) formData.append('groupMeUrl', data.groupMeUrl ?? '');
    if (data.name) formData.append('name', data.name ?? '');
    if (data.tags && Array.isArray(data.tags)) formData.append('tags', JSON.stringify(data.tags));
    if (isCapacityExceeded) formData.append('isCapacityExceeded', 'true');

    if (data.payModelFile) {
      formData.append('payModelPdf', data.payModelFile);
    }
    if (data.eventLogisticsFile) {
      formData.append('eventLogisticPdf', data.eventLogisticsFile);
    }

    await api.fetchData(endpoints.EDIT_EVENT.get({ id: params.id }), formData);

    onClose({ updated: true });
  };

  const getTemplates = async (venueId: string) => {
    const resp = templateApi.fetchData(endpoints.SEARCH_TEMPLATES.get({ venueId }));
    return resp;
  };

  useEffect(() => {
    api.fetchData(endpoints.GET_EVENT.get(params));
  }, []);

  useEffect(() => {
    if (api.loaded) {
      const {
        name,
        venue_id,
        allow_registrations_at,
        held_on,
        report_time,
        paymodel_url,
        event_logistics_url,
        event_training_url,
        hireflix_url,
        group_me_url,
        tags,
        venue,
        employee_capacity,
        buffer,
        automate_roster,
        template_id
      } = api.response.data;

      const selectedEventType = (() => {
        const current = api.response.data?.event_type as string | undefined;
        const allowed = new Set((schema.eventType.options as any[]).map((o: any) => o.value));
        return current && allowed.has(current) ? current : '';
      })();

      const data = {
        name: name,
        venue: { id: venue_id, timezone: venue.timezone },
        allowRegistrationsAt: toZonedTime(allow_registrations_at, venue.timezone),
        heldOn: toZonedTime(held_on, venue.timezone),
        heldOnTimeIsUnknown: api.response.data?.held_on_time_is_unknown ?? false,
        eventType: selectedEventType,
        employeeCapacity: employee_capacity && employee_capacity > 0 ? String(employee_capacity) : '',
        buffer: buffer && buffer > 0 ? String(buffer) : '',
        reportTimeAt: report_time ? toZonedTime(report_time, venue.timezone) : null,
        reportTimeIsUnknown: api.response.data?.report_time_is_unknown ?? false,
        payModelUrl: paymodel_url,
        eventLogisticsUrl: event_logistics_url,
        eventTrainingUrl: event_training_url,
        hireflixUrl: hireflix_url,
        groupMeUrl: group_me_url,
        automateRoster: automate_roster,
        tags: tags.map((item: any) => ({ label: item.name, value: item.id })),
        templateId: template_id ? String(template_id) : ''
      };

      getTemplates(formHandler.data.venue.id ? formHandler.data.venue.id : venue_id).then(res => {
        formHandler.setFullSchema(
          {
            ...schema,
            name: { ...schema.name, disabled: false },
            venue: { ...schema.venue, defaultLabel: venue.name, disabled: false },
            templateId: { ...schema.templateId, disabled: false, options: res.items },
            tags: { ...schema.tags, disabled: false },
            heldOn: { ...schema.heldOn, disabled: false },
            allowRegistrationsAt: { ...schema.allowRegistrationsAt, disabled: false },
            employeeCapacity: { ...schema.employeeCapacity, disabled: false },
            buffer: { ...schema.buffer, disabled: false },
            reportTimeAt: { ...schema.reportTimeAt, disabled: false },
            payModelFile: {
              ...schema.payModelFile,
              disabled: false,
              existingFile: paymodel_url
                ? {
                    name: paymodel_url.includes('/')
                      ? paymodel_url
                          .split('/')
                          .pop()
                          ?.replace(/\.pdf$/i, '') || 'Pay Model'
                      : paymodel_url,
                    url: paymodel_url
                  }
                : null
            },
            eventLogisticsFile: {
              ...schema.eventLogisticsFile,
              disabled: false,
              existingFile: event_logistics_url
                ? {
                    name: event_logistics_url.includes('/')
                      ? event_logistics_url
                          .split('/')
                          .pop()
                          ?.replace(/\.pdf$/i, '') || 'Event Logistics'
                      : event_logistics_url,
                    url: event_logistics_url
                  }
                : null
            },
            eventTrainingUrl: { ...schema.eventTrainingUrl, disabled: false },
            hireflixUrl: { ...schema.hireflixUrl, disabled: false },
            groupMeUrl: { ...schema.groupMeUrl, disabled: false },
            automateRoster: { ...schema.automateRoster, disabled: false }
          },
          data
        );
        setVenueId(venue_id);
      });
    }
  }, [api.loaded]);

  useEffect(() => {
    if (formHandler.data.venue.id !== venueId) {
      const venueId = formHandler.data.venue.id;

      if (venueId) {
        getTemplates(venueId).then(res => {
          const schema = {
            ...formHandler.schema,
            templateId: { ...formHandler.schema.templateId, options: res.items }
          };
          const data = { ...formHandler.data, templateId: '' };
          formHandler.setFullSchema(schema, data);
          setVenueId(venueId);
        });
      }
    }
  }, [formHandler.data.venue.id]);

  return (
    <Base
      header="Edit event"
      closing={closing}
      onClose={onClose}
      size="md"
      footer={
        <Button text="Save changes" disabled={!formHandler.valid || formHandler.isSubmitting} onClick={handleSubmit} />
      }
    >
      <DynamicForm handler={formHandler} />
    </Base>
  );
};

export default EditionEventModal;
